import React, { FC, useEffect, useContext } from 'react';
import _ from 'lodash';
import { formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
// import { getConfig } from 'utils/helperMethods';
import { Skeleton } from 'antd';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
//import ErrorBox from '../ErrorBox';

if (typeof window !== 'undefined') {
    var G2 = require('@antv/g2');
    var { Chart } = G2;
}

interface Props {
    dataset: Array<any>;
    chartContainer: string;
    padding?: [number, number, number, number];
    height: number;
    width?: number;
    fields?: Array<string>;
    barWidth?: string;
    YaxisConfig?: boolean;
    colorList?: Array<string>;
    positiveNegativeConfig?: boolean;
    minBound?: number;
    maxBound?: number;
    hover?: boolean;
    loading?: boolean;
    error?: any;
    enableOnClick?: boolean;
    preIntervalLabel?: string;
    postIntervalLabel?: string;
}

const limit = 11;
const SimulationBarChart: FC<Props> = ({
    dataset,
    chartContainer,
    height,
    width,
    fields = ['type', 'change'],
    barWidth = '20',
    YaxisConfig = true,
    colorList = ['#E6E6EA', '#DC3826', '#10AB5E', '#DC3826', '#2E9AFD'],
    positiveNegativeConfig = false,
    minBound,
    maxBound,
    hover = true,
    loading = false,
    enableOnClick = false,
    error,
    preIntervalLabel = '',
    postIntervalLabel = '',
}) => {
    const isWindowTabActive = useTrackTabChange();
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const {
        graphFontSize,
        graphLabelColor,
        graphFontSizeAlt,
        graphLabelColorAlt,
        graphFontFamily,
        decimalForNonPercentage,
    } = commonSetting;

    // if ((dataset && dataset.length === 0) || error) {
    //     return <ErrorBox msg={error ? String(error) : 'No Data'} />;
    // }
    useEffect(() => {
        if (!window) return;

        const chart = new Chart({
            container: chartContainer,
            width: width,
            height: height,
            autoFit: !width,
            appendPadding: YaxisConfig ? [0, 0, 0, 0] : [41, 0, 0, 0],
        });

        chart.scale({
            change: {
                min: minBound,
                max: maxBound,
                nice: true,
            },
        });
        chart.data(dataset);

        chart.axis('type', {
            tickLine: null,
            style: {
                fontSize: graphFontSize,
                fill: graphLabelColor,
                fontFamily: graphFontFamily,
            },

            label: false,
            // label: {
            //     autoHide: false,
            //     tooltip: true,
            //     formatter: (val: any) => {
            //         let str = '';
            //         //adding newline (\n) to string depending on the length
            //         if (val.length <= limit) str = trimStringToNewline(val, limit, 1);
            //         else str = trimStringToNewline(val, limit, 0);
            //         return str;
            //         return 'asd';
            //     },
            //     // layout: 'limitInShape',

            //     style: {
            //         fontSize: graphFontSizeAlt,
            //         fill: graphLabelColor,
            //         fontFamily: graphFontFamily,
            //     },
            // },
        });
        if (YaxisConfig) {
            chart.axis('change', {
                grid: null,
                tickLine: null,
                style: {
                    fontSize: graphFontSize,
                    fill: graphLabelColor,
                    fontFamily: graphFontFamily,
                },
                label: {
                    formatter: (val: any) => {
                        return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                    },
                    style: {
                        fontSize: graphFontSize,
                        fill: graphLabelColor,
                        fontFamily: graphFontFamily,
                    },
                },
            });
        } else {
            chart.axis('change', false);
        }
        if (!positiveNegativeConfig) {
            chart.legend(false);
            chart.interaction('active-region');
            chart.interval().position('type*change').size(barWidth).color('type', colorList);
        } else {
            chart
                .interval()
                .position('type*change')
                .size(barWidth)
                .color('change', (change: any) => {
                    if (change[1] > change[0]) {
                        return colorList[0];
                    } else {
                        return colorList[1];
                    }
                })
                .style(fields[0], (val: any) => {
                    return {
                        cursor: enableOnClick ? 'pointer' : 'auto',
                    };
                })
                .label('change', (change: any) => {
                    const formattedNum = change[1] - change[0];
                    return {
                        content:
                            preIntervalLabel +
                            formatNumWithKMBAlt(formattedNum, decimalForNonPercentage, decimalForNonPercentage) +
                            postIntervalLabel,
                        offsetY: formattedNum < 0 ? -5 : 5,
                        style: {
                            cursor: enableOnClick ? 'pointer' : 'auto',
                            fontSize: graphFontSizeAlt,
                            fill: graphLabelColorAlt,
                            fontFamily: graphFontFamily,
                        },
                    };
                });
        }
        chart.legend(false);
        if (!hover) {
            chart.tooltip(false);
        }
        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, isWindowTabActive]);
    return (
        <>
            {loading && (
                <div style={{ height: '100%' }}>
                    <Skeleton active />
                    <Skeleton active />
                </div>
            )}
            <div style={{ display: loading ? 'none' : 'block' }} className="w-full" id={chartContainer} />
        </>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    return _.isEqual(prevProps, nextProps);
}

export default React.memo(SimulationBarChart, arePropsEqual);
